// MarketingVolante — /admin/marketing/volante
// Builder modular de material impreso para edificios.
// PREVIEW UNIFICADO: el panel derecho muestra el SVG REAL que se descarga.
// Una sola fuente de verdad (src/lib/volanteRenderer.js).

import { useState, useEffect } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { Link } from 'react-router-dom'
import { renderVolante, inyectarLogo, FORMATOS } from '../../lib/volanteRenderer'

const FORMATOS_LABEL = {
  A4: 'A4 hall edificio',
  A5: 'A5 en mano',
  A6: 'A6 buzón',
  tarjeta: 'Tarjeta portero 90×55mm',
}

const CATALOGO = {
  header: { label: 'Header (logo Limpack)', fijo: true, props: { eyebrow: 'EDIFICIO POCITOS' } },
  hero: {
    label: 'Hero · titular split',
    props: {
      linea1: 'Si tu edificio',
      linea2: 'se suma,',
      linea3: 'pagás menos.',
      linea3_teal: true,
      pill: 'PARA TU EDIFICIO',
    },
  },
  descuento: {
    label: 'Card descuento vecinos',
    props: { umbral: 10, porcentaje: 5, eyebrow: 'PRECIO BASE', precio_base: 'Mejor precio que el promedio del mercado' },
  },
  pasos: {
    label: 'Pasos visuales 1 → 10 → −5%',
    props: { p1_num: '1', p1_label: 'vecino lo descubre', p2_num: '10', p2_label: 'se suman en tu cuadra', p3_num: '−5%', p3_label: 'para todos, todos los meses' },
  },
  lista: { label: 'Lista productos', props: { items: ['Jabón ropa 2L', 'Detergente vajilla', 'Lavandina', 'Bolsas basura', 'Papel cocina', 'Multiuso'] } },
  testimonio: { label: 'Testimonio · quote', props: { quote: 'Me llega el día que pido y ya está.', atrib: 'Florencia, Pocitos' } },
  chips: { label: 'Chips beneficios', props: { chips: ['Sin permanencia', 'Sin contrato', 'Envío gratis'] } },
  subcopy: { label: 'Sub-copy explicativo', props: { texto: 'Tu kit tiene lo que tu casa gasta en un mes, armado para tu hogar y entregado en tu puerta.' } },
  qr: { label: 'QR + CTA cierre', fijo: true, props: { eyebrow: 'SUMATE A LA LISTA', titulo: 'Escaneá. Sin compromiso.', bajada: 'Cuando se sumen 10 en tu cuadra, todos arrancan con el 5%.' } },
  footer: { label: 'Footer · handle + tagline', fijo: true, props: {} },
}

const PRESETS = {
  vecinos_descuento: { label: 'Vecinos + descuento', orden: ['header', 'hero', 'descuento', 'pasos', 'qr', 'footer'] },
  lanzamiento: { label: 'Lanzamiento', orden: ['header', 'hero', 'lista', 'chips', 'qr', 'footer'] },
  testimonio: { label: 'Testimonios', orden: ['header', 'hero', 'testimonio', 'chips', 'qr', 'footer'] },
  tarjeta_portero: { label: 'Compacto', orden: ['header', 'hero', 'qr', 'footer'] },
}

// ────────── MAIN ──────────

export default function MarketingVolante() {
  const [formato, setFormato] = useState('A4')
  const [loteSlug, setLoteSlug] = useState('a4-pocitos-001')
  const [orden, setOrden] = useState(PRESETS.vecinos_descuento.orden)
  const [propsMap, setPropsMap] = useState(() => {
    const m = {}
    for (const [k, v] of Object.entries(CATALOGO)) m[k] = { ...v.props }
    return m
  })
  const [bloqueEditando, setBloqueEditando] = useState(null)
  const [svg, setSvg] = useState('')
  const [bajando, setBajando] = useState(false)
  const [errMsg, setErrMsg] = useState(null)
  const [logoUri, setLogoUri] = useState(null)

  const fmt = FORMATOS[formato]

  function cambiarFormato(nuevo) {
    setFormato(nuevo)
    if (nuevo === 'tarjeta' || nuevo === 'A6') setOrden(['header', 'hero', 'qr', 'footer'])
    else if (nuevo === 'A5' && orden.length > 5) setOrden(PRESETS.vecinos_descuento.orden)
  }

  // Carga el logo dark (manual de marca: fondo Navy → wordmark blanco, gota teal)
  useEffect(() => {
    let cancelled = false
    fetch('/logo-horizontal-compacto-dark.png')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.blob()
      })
      .then(blob => new Promise(res => {
        const fr = new FileReader()
        fr.onload = () => res(fr.result)
        fr.readAsDataURL(blob)
      }))
      .then(uri => { if (!cancelled) setLogoUri(uri) })
      .catch(e => { if (!cancelled) setErrMsg('No pude cargar el logo: ' + e.message) })
    return () => { cancelled = true }
  }, [])

  // Re-genera el SVG cada vez que cambia algo (incluyendo cuando el logo carga)
  useEffect(() => {
    let cancelled = false
    const blocks = orden.map(tipo => ({ tipo, props: propsMap[tipo] }))
    renderVolante({ formato, lote_slug: loteSlug || 'sin-slug', blocks })
      .then(({ svg: rawSvg }) => {
        if (cancelled) return
        setSvg(logoUri ? inyectarLogo(rawSvg, logoUri) : rawSvg)
      })
      .catch(e => { if (!cancelled) setErrMsg(e.message) })
    return () => { cancelled = true }
  }, [formato, loteSlug, orden, propsMap, logoUri])

  function toggleBloque(tipo) {
    if (CATALOGO[tipo]?.fijo) return
    setOrden(prev => {
      if (prev.includes(tipo)) return prev.filter(x => x !== tipo)
      const order = ['header', 'hero', 'descuento', 'pasos', 'lista', 'testimonio', 'chips', 'subcopy', 'qr', 'footer']
      return [...prev, tipo].sort((a, b) => order.indexOf(a) - order.indexOf(b))
    })
  }

  function moverBloque(idx, dir) {
    setOrden(prev => {
      const next = [...prev]
      const swap = idx + dir
      if (swap < 0 || swap >= next.length) return prev
      if (CATALOGO[next[idx]]?.fijo || CATALOGO[next[swap]]?.fijo) return prev
      ;[next[idx], next[swap]] = [next[swap], next[idx]]
      return next
    })
  }

  function aplicarPreset(presetKey) {
    setOrden(PRESETS[presetKey].orden)
    setBloqueEditando(null)
  }

  function updateProp(bloque, key, value) {
    setPropsMap(prev => ({ ...prev, [bloque]: { ...prev[bloque], [key]: value } }))
  }

  // Conversión SVG → blob para canvas
  async function svgABlob(dpi) {
    if (!svg) throw new Error('No hay SVG generado todavía')
    const mmToPx = dpi / 25.4
    const pxW = Math.round(fmt.w * mmToPx)
    const pxH = Math.round(fmt.h * mmToPx)
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const img = new Image()
    await new Promise((res, rej) => {
      img.onload = res
      img.onerror = () => rej(new Error('No pude cargar el SVG en una imagen'))
      img.src = url
    })
    const canvas = document.createElement('canvas')
    canvas.width = pxW
    canvas.height = pxH
    const ctx = canvas.getContext('2d')
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, pxW, pxH)
    URL.revokeObjectURL(url)
    return { canvas, pxW, pxH }
  }

  async function descargarPng() {
    setBajando(true); setErrMsg(null)
    try {
      const { canvas } = await svgABlob(300)
      canvas.toBlob(b => {
        if (!b) { setErrMsg('No se pudo generar el PNG'); return }
        const a = document.createElement('a')
        a.href = URL.createObjectURL(b)
        a.download = `volante-${formato}-${loteSlug || 'sin-slug'}.png`
        a.click()
        setTimeout(() => URL.revokeObjectURL(a.href), 2000)
      }, 'image/png')
    } catch (e) { setErrMsg(e.message) }
    finally { setBajando(false) }
  }

  async function descargarPdf() {
    setBajando(true); setErrMsg(null)
    try {
      const { canvas } = await svgABlob(200)
      const pngDataUrl = canvas.toDataURL('image/png')
      const jsPdfModule = await import('jspdf')
      const JsPdf = jsPdfModule.jsPDF || jsPdfModule.default
      const pdf = new JsPdf({
        unit: 'mm',
        format: [fmt.w, fmt.h],
        orientation: fmt.w > fmt.h ? 'landscape' : 'portrait',
        compress: true,
      })
      pdf.addImage(pngDataUrl, 'PNG', 0, 0, fmt.w, fmt.h, undefined, 'FAST')
      pdf.save(`volante-${formato}-${loteSlug || 'sin-slug'}.pdf`)
    } catch (e) { setErrMsg(e.message) }
    finally { setBajando(false) }
  }

  function abrirSvg() {
    if (!svg) return
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' })
    window.open(URL.createObjectURL(blob), '_blank')
  }

  const disponibles = Object.keys(CATALOGO).filter(k => !orden.includes(k))

  return (
    <AdminLayout title="Material edificios">
      <div className="mv-wrap">
        <header className="mv-head">
          <div>
            <div className="mv-eyebrow">MARKETING · MATERIAL IMPRESO</div>
            <h1 className="mv-title">Builder de volantes</h1>
            <div className="mv-sub">Lo que ves en el preview <strong>es exactamente</strong> lo que descargás.</div>
          </div>
          <Link to="/admin/marketing" className="mv-btn-ghost">← Hub Marketing</Link>
        </header>

        <div className="mv-cols">
          {/* PANEL IZQUIERDA */}
          <div className="mv-panel">
            <div className="mv-section">
              <div className="mv-section-title">Formato</div>
              <select value={formato} onChange={e => cambiarFormato(e.target.value)} className="mv-select">
                {Object.entries(FORMATOS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>

            <div className="mv-section">
              <div className="mv-section-title">Lote slug</div>
              <input
                type="text"
                value={loteSlug}
                onChange={e => setLoteSlug(e.target.value.replace(/[^a-z0-9-]/gi, '-').toLowerCase())}
                className="mv-input"
                placeholder="a4-pocitos-001"
              />
              <div className="mv-hint">QR apuntará a <code>limpack.uy/r/{loteSlug || '…'}</code></div>
            </div>

            <div className="mv-section">
              <div className="mv-section-title">Plantillas rápidas</div>
              <div className="mv-presets">
                {Object.entries(PRESETS).map(([k, p]) => (
                  <button key={k} onClick={() => aplicarPreset(k)} className="mv-preset">{p.label}</button>
                ))}
              </div>
            </div>

            <div className="mv-section">
              <div className="mv-section-title">Activos · {orden.length} bloques</div>
              <div className="mv-bloques-activos">
                {orden.map((tipo, idx) => (
                  <div key={tipo} className={`mv-bloque-activo ${CATALOGO[tipo]?.fijo ? 'fijo' : ''}`}>
                    <div className="mv-bloque-info">
                      <span className="mv-bloque-orden">{idx + 1}</span>
                      <span className="mv-bloque-label">{CATALOGO[tipo].label}</span>
                      {CATALOGO[tipo]?.fijo && <span className="mv-bloque-fijo-tag">fijo</span>}
                    </div>
                    <div className="mv-bloque-acciones">
                      <button onClick={() => moverBloque(idx, -1)} disabled={CATALOGO[tipo]?.fijo} title="Subir">▲</button>
                      <button onClick={() => moverBloque(idx, +1)} disabled={CATALOGO[tipo]?.fijo} title="Bajar">▼</button>
                      <button onClick={() => setBloqueEditando(tipo === bloqueEditando ? null : tipo)} title="Editar" className={tipo === bloqueEditando ? 'mv-editing' : ''}>✎</button>
                      {!CATALOGO[tipo]?.fijo && <button onClick={() => toggleBloque(tipo)} className="mv-rm" title="Sacar">×</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {disponibles.length > 0 && (
              <div className="mv-section">
                <div className="mv-section-title">Sumar bloques</div>
                <div className="mv-bloques-disp">
                  {disponibles.map(tipo => (
                    <button key={tipo} onClick={() => toggleBloque(tipo)} className="mv-disp-btn">
                      + {CATALOGO[tipo].label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {bloqueEditando && (
              <div className="mv-section mv-editor">
                <div className="mv-section-title">Editar: {CATALOGO[bloqueEditando].label}</div>
                {Object.entries(propsMap[bloqueEditando]).map(([k, v]) => (
                  <div key={k} className="mv-prop">
                    <label className="mv-prop-label">{k.replace(/_/g, ' ')}</label>
                    {Array.isArray(v) ? (
                      <textarea
                        value={v.join('\n')}
                        onChange={e => updateProp(bloqueEditando, k, e.target.value.split('\n').filter(s => s.trim()))}
                        rows={Math.max(3, v.length + 1)}
                        className="mv-textarea"
                      />
                    ) : typeof v === 'boolean' ? (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <input type="checkbox" checked={v} onChange={e => updateProp(bloqueEditando, k, e.target.checked)} />
                        <span style={{ fontSize: 12, color: '#1A3461' }}>{v ? 'activo' : 'desactivado'}</span>
                      </label>
                    ) : typeof v === 'number' ? (
                      <input type="number" value={v} onChange={e => updateProp(bloqueEditando, k, Number(e.target.value))} className="mv-input" />
                    ) : (
                      <input type="text" value={v || ''} onChange={e => updateProp(bloqueEditando, k, e.target.value)} className="mv-input" />
                    )}
                  </div>
                ))}
                <button onClick={() => setBloqueEditando(null)} className="mv-btn-ghost" style={{ width: '100%', marginTop: 8 }}>Cerrar editor</button>
              </div>
            )}

            <div className="mv-section">
              <div className="mv-section-title">Descargar</div>
              <button onClick={descargarPdf} disabled={bajando || !svg} className="mv-btn-primary">
                {bajando ? 'Generando…' : '⬇ Descargar PDF para imprenta'}
              </button>
              <button onClick={descargarPng} disabled={bajando || !svg} className="mv-btn-ghost" style={{ marginTop: 6, width: '100%' }}>
                🖼 Descargar PNG (300 dpi)
              </button>
              <button onClick={abrirSvg} disabled={!svg} className="mv-btn-ghost" style={{ marginTop: 6, width: '100%' }}>
                🔍 Abrir SVG en pestaña nueva
              </button>
              {errMsg && <div className="mv-err">{errMsg}</div>}
              <div className="mv-hint" style={{ marginTop: 8 }}>
                Antes de imprimir, creá el lote en <Link to="/admin/marketing/lotes">/admin/marketing/lotes</Link> con el mismo slug.
              </div>
            </div>
          </div>

          {/* PANEL DERECHA — PREVIEW SVG REAL */}
          <div className="mv-preview-wrap">
            <div className="mv-preview-bg">
              <div className="mv-volante" style={{ width: previewWidth(formato), aspectRatio: `${fmt.w} / ${fmt.h}` }}>
                {svg ? (
                  <div className="mv-svg-wrap" dangerouslySetInnerHTML={{ __html: svg }} />
                ) : (
                  <div className="mv-loading">Generando preview…</div>
                )}
              </div>
              <div className="mv-volante-meta">
                <strong>{FORMATOS_LABEL[formato]}</strong> · {fmt.w}×{fmt.h}mm · slug: <code>{loteSlug || 'sin-slug'}</code>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Estilos />
    </AdminLayout>
  )
}

function previewWidth(formato) {
  switch (formato) {
    case 'A4': return 460
    case 'A5': return 360
    case 'A6': return 280
    case 'tarjeta': return 460
    default: return 460
  }
}

function Estilos() {
  return (
    <style>{`
      .mv-wrap { max-width: 1500px; margin: 0 auto; padding: 0 4px 60px; }
      .mv-head { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 12px 16px; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
      .mv-eyebrow { font-size: 10.5px; letter-spacing: .8px; color: #6B7280; }
      .mv-title { margin: 2px 0 0; font-size: 18px; font-weight: 600; color: #1A3461; }
      .mv-sub { font-size: 11.5px; color: #6B7280; margin-top: 2px; }
      .mv-sub strong { color: #00A8B5; }
      .mv-btn-ghost { padding: 8px 14px; border-radius: 8px; font-size: 13px; background: #fff; color: #1A3461; border: 1px solid #E6E2D8; text-decoration: none; cursor: pointer; font-family: inherit; }
      .mv-btn-ghost:hover { background: #F8F6F1; }
      .mv-btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

      .mv-cols { display: grid; grid-template-columns: 380px 1fr; gap: 12px; align-items: start; }
      @media (max-width: 980px) { .mv-cols { grid-template-columns: 1fr; } }

      .mv-panel { background: #fff; border: 1px solid #E6E2D8; border-radius: 10px; padding: 14px; position: sticky; top: 12px; max-height: calc(100vh - 24px); overflow-y: auto; }
      .mv-section { margin-bottom: 16px; }
      .mv-section-title { font-size: 10.5px; letter-spacing: .8px; color: #6B7280; margin-bottom: 6px; font-weight: 600; text-transform: uppercase; }
      .mv-select, .mv-input, .mv-textarea { width: 100%; padding: 8px 10px; border: 1px solid #E6E2D8; border-radius: 6px; font-size: 13px; font-family: inherit; background: #fff; color: #1A3461; box-sizing: border-box; }
      .mv-textarea { resize: vertical; font-family: inherit; }
      .mv-hint { font-size: 10.5px; color: #6B7280; margin-top: 4px; line-height: 1.4; }
      .mv-hint code { background: #F8F6F1; padding: 1px 4px; border-radius: 3px; color: #00A8B5; }

      .mv-presets { display: flex; flex-wrap: wrap; gap: 4px; }
      .mv-preset { padding: 5px 10px; font-size: 11px; border-radius: 6px; background: #F8F6F1; color: #1A3461; border: 1px solid #E6E2D8; cursor: pointer; font-family: inherit; }
      .mv-preset:hover { background: #E0F4F6; border-color: #00A8B5; }

      .mv-bloques-activos { display: flex; flex-direction: column; gap: 4px; }
      .mv-bloque-activo { display: flex; justify-content: space-between; align-items: center; padding: 6px 8px; background: #F8F6F1; border-radius: 6px; border-left: 3px solid #00A8B5; }
      .mv-bloque-activo.fijo { border-left-color: #C5A059; background: #FFFBEF; }
      .mv-bloque-info { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; }
      .mv-bloque-orden { background: #1A3461; color: #fff; font-size: 10px; padding: 1px 5px; border-radius: 3px; font-weight: 700; flex-shrink: 0; }
      .mv-bloque-label { font-size: 11.5px; color: #1A3461; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .mv-bloque-fijo-tag { font-size: 8.5px; background: #C5A059; color: #fff; padding: 1px 4px; border-radius: 3px; flex-shrink: 0; }
      .mv-bloque-acciones { display: flex; gap: 2px; flex-shrink: 0; }
      .mv-bloque-acciones button { background: #fff; border: 1px solid #E6E2D8; border-radius: 4px; font-size: 10px; padding: 2px 6px; cursor: pointer; color: #1A3461; min-width: 22px; }
      .mv-bloque-acciones button:disabled { opacity: 0.3; cursor: not-allowed; }
      .mv-bloque-acciones button:hover:not(:disabled) { background: #E0F4F6; }
      .mv-bloque-acciones button.mv-editing { background: #FFFBEF; border-color: #C5A059; color: #C5A059; }
      .mv-rm { color: #DC2626 !important; font-weight: 700; }

      .mv-bloques-disp { display: flex; flex-direction: column; gap: 4px; }
      .mv-disp-btn { padding: 6px 10px; font-size: 11.5px; background: #fff; color: #1A3461; border: 1px dashed #B5B0A0; border-radius: 6px; cursor: pointer; text-align: left; font-family: inherit; }
      .mv-disp-btn:hover { background: #F8F6F1; border-color: #00A8B5; border-style: solid; }

      .mv-editor { background: #FFFBEF; border: 1px solid #C5A059; border-radius: 8px; padding: 12px; }
      .mv-prop { margin-bottom: 8px; }
      .mv-prop-label { display: block; font-size: 10.5px; color: #6B7280; margin-bottom: 2px; text-transform: capitalize; }
      .mv-btn-primary { width: 100%; padding: 10px; background: #00A8B5; color: #fff; border: 0; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit; }
      .mv-btn-primary:disabled { background: #94A3B8; cursor: not-allowed; }
      .mv-err { margin-top: 8px; padding: 8px; background: #FEE2E2; border: 1px solid #DC2626; border-radius: 6px; font-size: 11.5px; color: #991B1B; }

      .mv-preview-wrap { display: flex; justify-content: center; }
      .mv-preview-bg { background: #F1EFE8; border: 1px solid #E6E2D8; border-radius: 10px; padding: 24px; text-align: center; width: 100%; box-sizing: border-box; }
      .mv-volante { position: relative; border-radius: 4px; overflow: hidden; box-shadow: 0 24px 60px rgba(0,0,0,0.25); background: #1A3461; margin: 0 auto; max-width: 100%; }
      .mv-svg-wrap { width: 100%; height: 100%; }
      .mv-svg-wrap > svg { display: block; width: 100%; height: 100%; }
      .mv-loading { padding: 40px; color: rgba(255,255,255,0.5); font-size: 14px; }
      .mv-volante-meta { margin-top: 14px; font-size: 12px; color: #5F5E5A; }
      .mv-volante-meta code { background: #fff; padding: 2px 6px; border-radius: 4px; color: #1A3461; }
    `}</style>
  )
}
